import { useMemo } from "react";
import { useIsDesktop } from "../../hooks/useMediaQuery";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getUserProfile, logout } from "../../services/authService";
import camcomLogo from "../../assets/logos/camcom-logo.png";
import DesignSwitcher from "../DesignSwitcher";
import { roleForAccessLevel } from "../../utils/userRoles";
import {
  MODULE_GROUP,
  MODULE_GROUP_LABEL,
  navSections,
  withClientModules,
} from "./moduleNav";
import { isNavPathActive } from "./navMatch";
import LineSwitcher from "../../components/LineSwitcher";
import { getSourceName } from "../../services/authService";
import { getClientEntry } from "../../config/clientConfig";

/**
 * Clearline sidebar — slim text nav, one blue accent, 208px fixed rail.
 * Spec: design_handoff_clearline_redesign/README.md § Screens › Sidebar.
 */

// Paths stay inside /redesign, so navigating the new design never drops you
// back into the old one mid-comparison. People used to be the exception — it
// pointed at /people and Layout swapped back to the old chrome — which is the
// seam this nav was apologising for. It has its own page now.
const NAV_ITEMS = [
  { id: "home", label: "Home", disabled: true },
  { id: "cases", label: "Cases", path: "/redesign/cases" },
  { id: "people", label: "Users", path: "/redesign/people" },
  { id: "analytics", label: "Analytics", path: "/redesign/analytics" },
];



/** "a.kalnina@camcom.ai" → "A. Kalnina" */
const displayNameFromEmail = (email) => {
  const local = String(email || "").split("@")[0];
  if (!local) return "Signed in";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return "Signed in";
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  }
  const first = parts[0];
  const last = parts[parts.length - 1];
  const initial = first.length === 1 ? `${first.toUpperCase()}.` : first;
  return `${initial.charAt(0).toUpperCase() + initial.slice(1)} ${
    last.charAt(0).toUpperCase() + last.slice(1)
  }`;
};

const initialsFromName = (name) => {
  const parts = String(name || "")
    .replace(/\./g, "")
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function Sidebar({ open = false, onNavigate }) {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  // Resolved once. It was being read per render AND again per nav item, and
  // getSourceName() goes to localStorage every time.
  const client = getClientEntry(getSourceName());
  const sections = useMemo(
    () => navSections(withClientModules(NAV_ITEMS, client)),
    [client],
  );

  const user = useMemo(() => {
    const profile = getUserProfile();
    const name = displayNameFromEmail(profile?.email);
    return {
      name,
      initials: initialsFromName(name),
      role:
        roleForAccessLevel(profile?.access_level) || "Claim handler",
    };
  }, []);

  const handleLogout = (event) => {
    event.preventDefault();
    logout();
    navigate("/");
  };

  return (
    <nav
      aria-label="Main"
      /* A transform still leaves the rail in the tab order and the
         accessibility tree, so with the drawer closed Tab walked into
         links nobody could see. inert removes both. Never at lg, where
         the rail is the real navigation. */
      {...(!open && !isDesktop ? { inert: true } : {})}
      className={[
        "flex w-[208px] flex-none flex-col border-r border-cl-card bg-cl-sidebar px-[14px] pb-4 pt-5 font-clearline",
        // Off-canvas below lg, the static rail at lg. 208px is right inside a
        // drawer; what was wrong was 208px of a 375px screen in flow, which
        // left the page 167px.
        "fixed inset-y-0 left-0 z-fixed shadow-xl transition-transform",
        // The rail is pinned to the viewport height here, so a long nav
        // (Hegic folds in Break-in and PAYD) needs somewhere to go.
        "overflow-y-auto",
        open ? "translate-x-0" : "-translate-x-full",
        "lg:static lg:h-full lg:translate-x-0 lg:shadow-none",
      ].join(" ")}
    >
      {/* Brand */}
      <Link to="/redesign/cases" className="flex items-center gap-[9px] px-2 pb-[22px]">
        {/* The artwork has no alpha: a 520x520 red square inset 10px, with the
            "EYE THE FUTURE" tagline beneath it on white. Clipped to the square
            and scaled slightly past the frame so the white surround never
            shows against the sidebar. */}
        <span className="flex h-[28px] w-[28px] flex-none overflow-hidden rounded-brand bg-cl-accent">
          <img
            src={camcomLogo}
            alt="CamCom"
            className="h-full w-full scale-[1.09] object-cover object-top"
          />
        </span>
        <span className="flex flex-col">
          <span className="text-[13.5px] font-bold leading-tight tracking-[-0.01em] text-cl-text">
            CamCom
          </span>
          <span className="text-[9.5px] font-semibold uppercase leading-tight tracking-[0.09em] text-cl-label">
            AI Portal
          </span>
        </span>
      </Link>

      {/* Nav */}
      <div className="flex flex-col">
        {sections.map((section, sectionIndex) => {
          const heading =
            section.group === MODULE_GROUP ? MODULE_GROUP_LABEL : null;
          const headingId = heading ? `nav-group-${section.group}` : undefined;

          return (
            <div
              key={section.group || "standard"}
              role={heading ? "group" : undefined}
              aria-labelledby={headingId}
              className={`flex flex-col gap-[2px]${
                sectionIndex === 0 ? "" : " mt-3"
              }`}
            >
              {heading && (
                <div
                  id={headingId}
                  className="mb-1 px-[10px] text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label"
                >
                  {heading}
                </div>
              )}

              {section.items.map((rawItem) => {
                /**
                 * Analytics is only real where the backend serves it. Left
                 * disabled elsewhere rather than linked to a page of invented
                 * figures.
                 */
                const item =
                  rawItem.id === "analytics" && !client?.hasAnalytics
                    ? { ...rawItem, path: undefined, disabled: true }
                    : rawItem;

                const isActive = isNavPathActive(location.pathname, item.path);

                const rowClass = [
                  "flex items-center justify-between gap-2 rounded-tile px-[10px] py-2 text-[13px] transition-colors",
                  item.disabled
                    ? "cursor-not-allowed font-medium text-cl-faint"
                    : isActive
                      ? "bg-cl-tint font-semibold text-cl-accent"
                      : "font-medium text-cl-secondary hover:bg-cl-hover",
                ].join(" ");

                const body = (
                  <>
                    <span>{item.label}</span>
                    {item.disabled && (
                      <span className="text-[9px] font-semibold tracking-[0.06em] text-cl-disabled">
                        SOON
                      </span>
                    )}
                  </>
                );

                return item.disabled ? (
                  <div key={item.id} aria-disabled="true" className={rowClass}>
                    {body}
                  </div>
                ) : (
                  <Link
                    key={item.id}
                    to={item.path}
                    aria-current={isActive ? "page" : undefined}
                    className={rowClass}
                  >
                    {body}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer: design switch + engine status + user */}
      <div className="mt-auto flex flex-col gap-[10px]">
        {/* Motor or property, for an Ergo admin. Renders nothing for a client
            with one line, or a user whose access level names a line already. */}
        <LineSwitcher variant="rail" />

        {/* The A/B toggle lives with the navigation now. Floating over the
            page, it covered whichever layout was being compared. */}
        <DesignSwitcher variant="rail" />

        <div className="rounded-[10px] border border-cl-card bg-cl-surface px-3 py-[10px]">
          <div className="mb-1 text-[10.5px] font-semibold tracking-[0.04em] text-cl-label">
            AI ENGINE
          </div>
          <div className="flex items-center gap-[6px] text-[12px] font-medium text-cl-text">
            <span className="h-[7px] w-[7px] flex-none rounded-pill bg-[#22a55c]" />
            Operational
          </div>
        </div>

        <div className="flex items-center gap-[9px] px-2 py-[6px]">
          <span className="flex h-7 w-7 flex-none items-center justify-center rounded-pill bg-cl-avatar text-[10.5px] font-bold text-cl-meta">
            {user.initials}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12px] font-semibold text-cl-text">
              {user.name}
            </span>
            <span className="block truncate text-[10.5px] text-cl-label">
              {user.role}
            </span>
          </span>
          <button
            type="button"
            onClick={handleLogout}
            className="flex-none text-[11px] font-medium text-cl-faint transition-colors hover:text-cl-secondary"
          >
            Log out
          </button>
        </div>
      </div>
    </nav>
  );
}
